import { Pressable, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { tokens } from '../../theme/tokens';
import type { KnowledgeGraphNodeStatus } from '../../types/learningAnalysis';
import type { KnowledgeGraphLayoutNode } from '../../utils/knowledgeGraphLayout';
import { AppText } from '../ui/AppText';

type KnowledgeGraphNodeProps = {
  node: KnowledgeGraphLayoutNode;
  selected?: boolean;
  compact?: boolean;
  onPress: (node: KnowledgeGraphLayoutNode) => void;
  style?: StyleProp<ViewStyle>;
};

const getToneStyle = (status: KnowledgeGraphNodeStatus) => {
  if (status === 'mastered') {
    return styles.masteredNode;
  }
  if (status === 'review') {
    return styles.reviewNode;
  }
  return styles.weakNode;
};

const getTone = (status: KnowledgeGraphNodeStatus) => {
  if (status === 'mastered') {
    return 'success';
  }
  if (status === 'review') {
    return 'warning';
  }
  return 'danger';
};

export function KnowledgeGraphNode({
  node,
  selected = false,
  compact = false,
  onPress,
  style,
}: KnowledgeGraphNodeProps) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${node.subject} ${node.label} ${node.masteryPercent}%`}
      onPress={() => onPress(node)}
      style={({ pressed }) => [
        styles.node,
        {
          left: node.x - node.radius,
          top: node.y - node.radius,
          width: node.radius * 2,
          height: node.radius * 2,
          borderRadius: node.radius,
        },
        getToneStyle(node.status),
        selected ? styles.selectedNode : null,
        pressed ? styles.pressed : null,
        style,
      ]}
    >
      <AppText
        variant="caption"
        weight="bold"
        align="center"
        numberOfLines={compact ? 2 : 3}
        style={styles.nodeTitle}
      >
        {node.label}
      </AppText>
      <AppText variant="caption" tone={getTone(node.status)} weight="bold" align="center">
        {node.masteryPercent}%
      </AppText>
      {!compact ? (
        <AppText variant="caption" tone="muted" align="center" numberOfLines={1}>
          {node.subject}
        </AppText>
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  node: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingHorizontal: tokens.spacing.xs,
    borderWidth: tokens.border.width,
    shadowColor: tokens.colors.text,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 3,
  },
  nodeTitle: {
    color: tokens.colors.text,
  },
  masteredNode: {
    borderColor: tokens.colors.success,
    backgroundColor: tokens.colors.successSoft,
  },
  reviewNode: {
    borderColor: tokens.colors.warning,
    backgroundColor: tokens.colors.warningSoft,
  },
  weakNode: {
    borderColor: tokens.colors.danger,
    backgroundColor: tokens.colors.dangerSoft,
  },
  selectedNode: {
    borderWidth: 2,
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.surface,
  },
  pressed: {
    opacity: 0.86,
  },
});
